import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { faStore, faTag, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import {
  DcFlexBox,
  DcLeftPanel,
  DcRightPanel,
  DcText,
  PixelBusinessDiagram,
  PixelIcon,
  PixelText,
  colors,
  DcButton,
} from 'pixel-base'

import CompanyInfo from './components/CompanyInfo'
import Stores from './components/Stores'
import { OnBoardingContext } from '../../../contexts/OnBoardingContext'

const OnBoarding = (props) => {
  const [searchParams] = useSearchParams()
  const { activeStep, setActiveStep } = React.useContext(OnBoardingContext)
  const currentUser = JSON.parse(localStorage.getItem('user_info') || '{}')
  const [localStep, setLocalStep] = useState(activeStep)
  const parsedQueryData = Object.fromEntries(searchParams.entries())

  // Set 'from' value only once on component mount
  useEffect(() => {
    if (parsedQueryData.from) {
      localStorage.setItem('from', parsedQueryData.from)
    } else {
      localStorage.setItem('from', localStorage.getItem('from') || 'normal')
    }
  }, [])

  const from = localStorage.getItem('from') || 'normal'

  // Track current view and store type for smooth navigation
  const [currentView, setCurrentView] = useState('store-selection') // 'store-selection', 'store-form', 'company-info'
  const [selectedStoreType, setSelectedStoreType] = useState(null) // 'shopify', 'daraz', 'woocommerce', 'manual'
  const [navigationHistory, setNavigationHistory] = useState<string[]>([])

  const steps = [
    {
      label: 'Store',
      icon: <PixelIcon icon={faStore} />,
      component: (
        <Stores
          {...props}
          from={from}
          onStoreTypeSelect={handleStoreTypeSelect}
        />
      ),
    },
  ]
  if (from !== 'settings') {
    steps.push({
      label: 'Company Info',
      icon: <PixelIcon icon={faTag} />,
      component: <CompanyInfo onBack={handleBackNavigation} />,
    })
  }

  // Handle store type selection
  function handleStoreTypeSelect(storeType) {
    setSelectedStoreType(storeType)
    setCurrentView('store-form')
    setNavigationHistory((prev) => [...prev, 'store-selection'])
  }

  // Handle back navigation
  function handleBackNavigation() {
    if (navigationHistory.length > 0) {
      const previousView = navigationHistory[navigationHistory.length - 1]
      setCurrentView(previousView)
      setNavigationHistory((prev) => prev.slice(0, -1))

      if (previousView === 'store-selection') {
        setSelectedStoreType(null)
      }
    }
  }

  // Handle "without website" option
  function handleWithoutWebsite() {
    setCurrentView('company-info')
    setNavigationHistory((prev) => [...prev, currentView])
  }

  // Combined effect to handle both step updates
  useEffect(() => {
    if (parsedQueryData.step) {
      const step = Number(parsedQueryData.step)
      if (step !== activeStep && setActiveStep) {
        setActiveStep(step)
      }
    }
    setLocalStep(activeStep)
  }, [activeStep, parsedQueryData.step, setActiveStep])

  // Render current view based on state
  const renderCurrentView = () => {
    switch (currentView) {
      case 'store-selection':
        return (
          <DcFlexBox flexDirection="column" width="100%">
            <Stores
              {...props}
              from={from}
              onStoreTypeSelect={handleStoreTypeSelect}
              onWithoutWebsite={handleWithoutWebsite}
            />
          </DcFlexBox>
        )
      case 'store-form':
        return (
          <DcFlexBox flexDirection="column" width="100%">
            <Stores
              {...props}
              from={from}
              selectedStoreType={selectedStoreType}
              onStoreTypeSelect={handleStoreTypeSelect}
              onWithoutWebsite={handleWithoutWebsite}
            />
          </DcFlexBox>
        )
      case 'company-info':
        return <CompanyInfo onBack={handleBackNavigation} />
      default:
        return steps[localStep]?.component
    }
  }

  return (
    <>
      <DcFlexBox
        width="100%"
        height="100vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor={colors.grayLighter}
      >
        <DcFlexBox
          border={`1px solid ${colors.grayBorderLight}`}
          boxShadow={`0 1px 0 ${colors.grayBorderLight}50`}
          width="1272px"
          height="684px"
          borderRadius="15px"
        >
          <DcLeftPanel
            backgroundColor={colors.authBg}
            borderRadius="15px 0 0 15px"
            width="59%"
            style={{
              overflow: 'hidden',
            }}
          >
            <PixelBusinessDiagram />
          </DcLeftPanel>
          <DcRightPanel
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            padding="40px"
            width="42%"
            borderRadius="0 15px 15px 0"
            backgroundColor={colors.white}
            position="relative"
          >
            {from !== 'settings' && (
              <DcFlexBox
                width="auto"
                height="auto"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                // margin="0 0 2rem 0"
              >
                <DcText
                  customColor={colors.grayTextDark}
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: '700',
                    fontSize: '1.25rem',
                    marginBottom: '6px',
                  }}
                >
                  Welcome, {currentUser?.name}
                </DcText>
                <PixelText
                  customColor={colors.grayMedium}
                  style={{
                    fontSize: '.875rem',
                    textAlign: 'center',
                  }}
                >
                  Get started with PixelOne
                </PixelText>
              </DcFlexBox>
            )}

            {/* Back Button */}
            {navigationHistory.length > 0 &&
              selectedStoreType !== 'daraz' &&
              selectedStoreType !== 'woocommerce' &&
              selectedStoreType !== 'shopify' && (
                <DcFlexBox
                  position="absolute"
                  top="25px"
                  left="25px"
                  width="100%"
                  justifyContent="flex-start"
                  height="auto"
                >
                  <DcButton
                    variant="link"
                    size="sm"
                    onClick={handleBackNavigation}
                    style={{
                      padding: '0px !important',
                    }}
                  >
                    <PixelIcon icon={faArrowLeft} />
                  </DcButton>
                </DcFlexBox>
              )}

            <DcFlexBox
              width="100%"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="auto"
            >
              {from === 'normal' ? (
                renderCurrentView()
              ) : (
                <Stores
                  from={from}
                  {...props}
                  onStoreTypeSelect={handleStoreTypeSelect}
                  onWithoutWebsite={handleWithoutWebsite}
                />
              )}
            </DcFlexBox>
          </DcRightPanel>
        </DcFlexBox>
      </DcFlexBox>
    </>
  )
}

export default OnBoarding
